

import { useState } from 'react'
import {TeacherDownloadTemplate, TeacherImport} from "services/teacherService";
import {VehicleDownloadTemplate, VehicleImport} from "services/vehicleService";
import {toast} from 'react-hot-toast'
import LoadingOverlay from "components/Static/LoadingOverlay";
import DownloadingOverlay from "components/Static/DownloadingOverlay";

export default function UploadPage() {
    const [message, setMessage] = useState('')
    const [isLoading, setIsLoading] = useState(false)
    const [isDownloading, setIsDownloading] = useState(false)

    const handleFileUpload = async (file: File, type: 'teachers' | 'vehicles') => {
        setIsLoading(true);
        setMessage('');

        try {
            // Validate file type
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'application/vnd.ms-excel.sheet.macroEnabled.12'
            ];

            if (!validTypes.includes(file.type)) {
                throw new Error('Chỉ chấp nhận file Excel (.xls, .xlsx, .xlsm)');
            }

            let response: any;

            if (type === 'teachers') {
                response = await TeacherImport(file);
            } else {
                response = await VehicleImport(file);
            }

            toast.success(response.message || 'Tải lên thành công');
            setMessage(response.message || 'Tải lên thành công');
        } catch (error: any) {
            setMessage(`Lỗi khi tải lên: ${error.message}`);
        } finally {
            setIsLoading(false);
        }
    };

    const handleDownloadTemplate = async (type: 'teachers' | 'vehicles') => {
        setIsDownloading(true)
        try {
            let blob: Blob;
            let fileName: string;

            switch (type) {
                case 'teachers':
                    blob = await TeacherDownloadTemplate();
                    fileName = 'Import-Teacher.xlsm';
                    break;
                case 'vehicles':
                    blob = await VehicleDownloadTemplate();
                    fileName = 'Import-Vehicle.xlsm';
                    break;
                default:
                    throw new Error('Loại tải xuống không hợp lệ');
            }

            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', fileName);
            document.body.appendChild(link);
            link.click();

            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            toast.success(`Tải xuống mẫu ${type === 'teachers' ? 'giáo viên' : 'phương tiện'} thành công`);
        } catch (error) {
            toast.error(`Lỗi khi tải xuống mẫu: ${error instanceof Error ? error.message : 'Đã xảy ra lỗi'}`);
        } finally {
            setIsDownloading(false);
        }
    }

    return (
        <div className="container mx-auto px-4 py-8">
            <div className="flex justify-between items-center mb-8">
                <h1 className="text-3xl font-bold text-gray-800">Tải lên dữ liệu</h1>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4">Tải lên danh sách Giáo viên</h2>
                    <p className="text-sm text-gray-600 mb-4">File Excel cần có 2 cột: "Tên giáo viên" và "Hạng đào tạo"</p>

                    <div className="space-y-4">
                        <input
                            type="file"
                            accept=".xlsx,.xls,.xlsm"
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'teachers')}
                            className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
                            disabled={isLoading}
                        />
                        <button
                            onClick={() => handleDownloadTemplate('teachers')}
                            disabled={isDownloading}
                            className="w-full bg-green-50 text-green-700 hover:bg-green-100 py-2 px-4 rounded-md font-semibold text-sm flex items-center justify-center"
                        >
                            {isDownloading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Đang tải...
                                </>
                            ) : 'Tải mẫu Giáo viên'}
                        </button>
                    </div>
                </div>

                <div className="bg-white rounded-lg shadow-md p-6">
                    <h2 className="text-xl font-semibold text-gray-700 mb-4">Tải lên danh sách Xe</h2>
                    <p className="text-sm text-gray-600 mb-4">File Excel cần có 2 cột: "Biển số xe" và "Hạng đào tạo"</p>

                    <div className="space-y-4">
                        <input
                            type="file"
                            accept=".xlsx,.xls,.xlsm"
                            onChange={(e) => e.target.files?.[0] && handleFileUpload(e.target.files[0], 'vehicles')}
                            className="block w-full text-sm text-gray-500
                file:mr-4 file:py-2 file:px-4
                file:rounded-md file:border-0
                file:text-sm file:font-semibold
                file:bg-blue-50 file:text-blue-700
                hover:file:bg-blue-100"
                            disabled={isLoading}
                        />
                        <button
                            onClick={() => handleDownloadTemplate('vehicles')}
                            disabled={isDownloading}
                            className="w-full bg-green-50 text-green-700 hover:bg-green-100 py-2 px-4 rounded-md font-semibold text-sm flex items-center justify-center"
                        >
                            {isDownloading ? (
                                <>
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Đang tải...
                                </>
                            ) : 'Tải mẫu Xe'}
                        </button>
                    </div>
                </div>
            </div>

            {isLoading  && <LoadingOverlay message="Đang tải dữ liệu..." />}

            {isDownloading && <DownloadingOverlay />}

            {message && (
                <div className={`mt-6 p-4 rounded-md whitespace-pre-line ${message.includes('thành công') ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {message.replace(/<br\s*\/?>/gi, '\n')}
                </div>
            )}
        </div>
    )
}